/**
 * Store Cluster Intelligence - AI-Driven Insights
 * =================================================
 * Shows which stores are underperforming their peer group
 * Actionable opportunities to close performance gaps
 */

import { useState, useEffect } from 'react';
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext';
import { useTenant } from '../contexts/TenantContext';
import GlobalHeader from '../components/GlobalHeader';
import { MapPin, TrendingUp, TrendingDown, Store, RefreshCw, BarChart3, Activity, ArrowRight, ShoppingBag, Truck, BookOpen, Package } from 'lucide-react';
import { Link } from 'wouter';

interface ClusterData {
  id: number;
  name: string;
  store_count: number;
  avg_revenue: number;
  avg_growth: number;
  performance_tier: string;
}

export default function StoreClusterIntelligence() {
  console.log('[StoreClusterIntelligence] Component loading - Portfolio-wide AI page');
  
  const { selectedFiscalWeek } = useFiscalWeekContext();
  const { selectedRetailerId } = useTenant();
  const [clusters, setClusters] = useState<ClusterData[]>([]);
  const [totalStores, setTotalStores] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchClusters = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('[StoreClusterIntelligence] Fetching clusters with retailer:', selectedRetailerId);
      const params = new URLSearchParams();
      
      if (selectedRetailerId) {
        params.append('retailer_id', selectedRetailerId);
      }
      
      const response = await fetch(`/api/stores/clusters?${params}`);
      const data = await response.json();
      
      console.log('[StoreClusterIntelligence] API Response:', data);
      
      if (data.success) {
        setClusters(data.clusters || []);
        setTotalStores(data.total_stores || 0);
      } else {
        setError(data.error || 'Failed to fetch clusters');
      }
    } catch (err: any) {
      console.error('[StoreClusterIntelligence] Fetch error:', err);
      setError(err.message || 'Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log('[StoreClusterIntelligence] Effect triggered - fetching clusters');
    fetchClusters();
  }, [selectedRetailerId]);

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'A': return 'text-green-600 bg-green-50 border-green-200';
      case 'B': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'C': return 'text-orange-600 bg-orange-50 border-orange-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getTierLabel = (tier: string) => {
    switch (tier) {
      case 'A': return 'Top Performer';
      case 'B': return 'Growing';
      case 'C': return 'Opportunity';
      default: return 'Standard';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <GlobalHeader 
        title="Store Cluster Intelligence"
        subtitle="AI-powered peer analysis and opportunity detection"
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* ML Health Monitor Link */}
        <Link href="/admin/ml-monitoring">
          <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-300 dark:border-purple-700 rounded-lg p-4 mb-6 cursor-pointer hover:border-purple-400 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500 rounded-lg">
                  <Activity className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">ML Infrastructure Health Monitor</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Check SageMaker endpoint status, Feature Store health, and model performance →</p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </Link>

        {/* Header Stats */}
        <div className="bg-white rounded-xl p-6 mb-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Portfolio Analysis</h2>
              <p className="text-gray-600 mt-1">
                Total Stores: <span className="font-semibold">{totalStores.toLocaleString()}</span>
              </p>
            </div>
            <button
              onClick={fetchClusters}
              disabled={isLoading}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              {isLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Analyzing store clusters...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-3">
              <MapPin className="w-6 h-6 text-red-600" />
              <div>
                <h3 className="font-semibold text-red-900">Error Loading Clusters</h3>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Clusters Grid */}
        {!isLoading && !error && clusters.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {clusters.map((cluster) => (
                <div
                  key={cluster.id}
                  className={`bg-white rounded-xl p-6 border-2 ${getTierColor(cluster.performance_tier)}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-lg ${getTierColor(cluster.performance_tier)}`}>
                        <Store className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">{cluster.name}</h3>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${getTierColor(cluster.performance_tier)}`}>
                          {getTierLabel(cluster.performance_tier)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 mt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Store Count</span>
                      <span className="font-bold text-gray-900">{cluster.store_count}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Avg Revenue</span>
                      <span className="font-bold text-gray-900">
                        ${(cluster.avg_revenue / 1000).toFixed(0)}K
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">YoY Growth</span>
                      <div className="flex items-center gap-1">
                        {cluster.avg_growth >= 0 ? (
                          <TrendingUp className="w-4 h-4 text-green-600" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-red-600" />
                        )}
                        <span className={`font-bold ${cluster.avg_growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {cluster.avg_growth.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <button 
                      onClick={() => {
                        // TODO: Navigate to store list filtered by cluster
                        alert(`View ${cluster.store_count} stores in "${cluster.name}" cluster\n\nThis will navigate to a detailed store list view (coming soon)`);
                      }}
                      className="w-full text-sm font-medium text-primary hover:text-primary/80 flex items-center justify-center gap-2 hover:bg-primary/5 py-2 rounded transition-colors"
                    >
                      <BarChart3 className="w-4 h-4" />
                      View Stores
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Cross-Agent Links */}
            <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Related Insights</h3>
              <p className="text-sm text-gray-600 mb-4">
                Use cluster intelligence to inform decisions across other agents:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Link href="/agents/merch-optimization">
                  <div className="p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer group">
                    <div className="flex items-center gap-3">
                      <ShoppingBag className="w-5 h-5 text-gray-400 group-hover:text-primary" />
                      <div>
                        <p className="font-medium text-gray-900 group-hover:text-primary">Merch Optimization</p>
                        <p className="text-xs text-gray-500">Optimize assortment by cluster</p>
                      </div>
                    </div>
                  </div>
                </Link>
                <Link href="/agents/supply-chain">
                  <div className="p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer group">
                    <div className="flex items-center gap-3">
                      <Truck className="w-5 h-5 text-gray-400 group-hover:text-primary" />
                      <div>
                        <p className="font-medium text-gray-900 group-hover:text-primary">Supply Chain</p>
                        <p className="text-xs text-gray-500">Allocation by cluster tier</p>
                      </div>
                    </div>
                  </div>
                </Link>
                <Link href="/agents/retail-story">
                  <div className="p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer group">
                    <div className="flex items-center gap-3">
                      <BookOpen className="w-5 h-5 text-gray-400 group-hover:text-primary" />
                      <div>
                        <p className="font-medium text-gray-900 group-hover:text-primary">Retail Story</p>
                        <p className="text-xs text-gray-500">Generate cluster narratives</p>
                      </div>
                    </div>
                  </div>
                </Link>
                <Link href="/inventory">
                  <div className="p-4 border border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer group">
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-gray-400 group-hover:text-primary" />
                      <div>
                        <p className="font-medium text-gray-900 group-hover:text-primary">Inventory</p>
                        <p className="text-xs text-gray-500">Stock levels by tier</p>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </>
        )}

        {/* Empty State */}
        {!isLoading && !error && clusters.length === 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Clusters Found</h3>
            <p className="text-gray-600">
              Clustering analysis will appear here once data is available.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
